import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { useMessageBox } from '../../contexts/MessageBoxContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  EvaluationCycle,
  closeEvaluationCycle,
  deleteEvaluationCycle,
  getEvaluationCycle,
  openEvaluationCycle,
} from '../../services/peoplePerformanceApi';
import { EvaluationCycleCalendarsSection } from './evaluationCycle/EvaluationCycleCalendarsSection';
import { EvaluationCycleFormsSection } from './evaluationCycle/EvaluationCycleFormsSection';
import { EvaluationCycleIndicatorsSection } from './evaluationCycle/EvaluationCycleIndicatorsSection';
import { EvaluationCycleSummarySection } from './evaluationCycle/EvaluationCycleSummarySection';

const CYCLE_STATUS_LABELS: Record<string, string> = {
  Draft: 'Rascunho',
  Open: 'Aberto',
  Closed: 'Encerrado',
};

export function EvaluationCycleDetailPage() {
  const { cycleId } = useParams();
  const navigate = useNavigate();
  const { confirmDelete } = useMessageBox();
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('people.evaluations');
  const [cycle, setCycle] = useState<EvaluationCycle | null>(null);
  const [cycleLoading, setCycleLoading] = useState(true);
  const [deletingCycle, setDeletingCycle] = useState(false);
  const [calendarCount, setCalendarCount] = useState<number | undefined>();
  const [formCount, setFormCount] = useState<number | undefined>();

  async function loadCycle(): Promise<void> {
    if (!cycleId) return;
    setCycleLoading(true);
    try {
      const result = await getEvaluationCycle(cycleId);
      setCycle(result);
    } catch {
      setCycle(null);
      toast.error('Falha ao carregar ciclo.');
    } finally {
      setCycleLoading(false);
    }
  }

  useEffect(() => {
    void loadCycle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId]);

  async function changeStatus(action: 'open' | 'close'): Promise<void> {
    if (!cycleId) return;
    try {
      const result = action === 'open' ? await openEvaluationCycle(cycleId) : await closeEvaluationCycle(cycleId);
      setCycle(result);
      toast.success(action === 'open' ? 'Ciclo aberto.' : 'Ciclo encerrado.');
    } catch {
      toast.error('Falha ao alterar status do ciclo.');
    }
  }

  async function handleDeleteCycle(): Promise<void> {
    if (!cycleId || !canWrite || !cycle) return;
    const statusLabel = CYCLE_STATUS_LABELS[cycle.status] ?? cycle.status;
    if (
      !(await confirmDelete({
        title: 'Excluir ciclo',
        message: `Excluir o ciclo "${cycle.name}"?`,
        details: [`Status atual: ${statusLabel}.`, 'Esta ação remove o ciclo da listagem.'],
      }))
    ) {
      return;
    }

    setDeletingCycle(true);
    try {
      await deleteEvaluationCycle(cycleId);
      toast.success('Ciclo excluído.');
      navigate('/people/evaluation-cycles');
    } catch {
      toast.error('Falha ao excluir ciclo.');
      setDeletingCycle(false);
    }
  }

  if (!cycleId) {
    return (
      <PageShell>
        <p className="text-secondary-500">Ciclo inválido.</p>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title={cycleLoading ? 'Carregando ciclo...' : cycle?.name ?? 'Ciclo não encontrado'}
        description="Calendários de coleta, indicadores, formulários e configuração do ciclo."
        actions={(
          <Link to="/people/evaluation-cycles" className="erp-btn-secondary text-sm">
            Voltar
          </Link>
        )}
      />

      <EvaluationCycleSummarySection
        cycle={cycle}
        loading={cycleLoading}
        canWrite={canWrite}
        deletingCycle={deletingCycle}
        calendarCount={calendarCount}
        formCount={formCount}
        onOpen={() => void changeStatus('open')}
        onClose={() => void changeStatus('close')}
        onDelete={() => void handleDeleteCycle()}
      />

      <EvaluationCycleCalendarsSection
        cycleId={cycleId}
        cycle={cycle}
        canWrite={canWrite}
        onCountChange={setCalendarCount}
        onCycleRefresh={() => void loadCycle()}
      />

      <EvaluationCycleIndicatorsSection
        cycleId={cycleId}
        cycle={cycle}
        cycleLoading={cycleLoading}
        canWrite={canWrite}
      />

      <EvaluationCycleFormsSection
        cycleId={cycleId}
        cycle={cycle}
        canWrite={canWrite}
        onCountChange={setFormCount}
      />
    </PageShell>
  );
}
